import { Dialog, useDialogTriggerContext } from "../../dialog";
import { Button, ButtonGroup } from "../../button";
import { forwardRef, useMemo } from "react";
import { Header } from "../../placeholders";
import { InfoIcon, WarningIcon } from "../../icons";
import { isNil, isNilOrEmpty, mergeProps, useChainedEventCallback, useSlots } from "../../shared";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "section";
export function InnerAlert({
  as = DefaultElement,
  autoFocusButton,
  cancelButtonLabel,
  children,
  forwardedRef,
  onCancelButtonClick,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  primaryButtonDisabled,
  primaryButtonLabel,
  secondaryButtonDisabled,
  secondaryButtonLabel,
  variant = "confirmation",
  zIndex = 1,
  ...rest
}) {
  const {
    close
  } = useDialogTriggerContext();
  const {
    content,
    heading
  } = useSlots(children, useMemo(() => ({
    _: {
      required: ["heading", "content"]
    },
    content: null,
    heading: null
  }), []));
  const handlePrimaryButtonClick = useChainedEventCallback(onPrimaryButtonClick, event => {
    if (!isNil(close)) {
      close(event);
    }
  });
  const handleSecondaryButtonClick = useChainedEventCallback(onSecondaryButtonClick, event => {
    if (!isNil(close)) {
      close(event);
    }
  });
  const handleCancelButtonClick = useChainedEventCallback(onCancelButtonClick, event => {
    if (!isNil(close)) {
      close(event);
    }
  });

  const warningIconMarkup = variant === "warning" && /*#__PURE__*/_jsx(Header, {
    children: /*#__PURE__*/_jsx(WarningIcon, {
      className: "o-ui-alert-warning-icon",
      size: "lg"
    })
  });

  const negativeIconMarkup = variant === "negative" && /*#__PURE__*/_jsx(Header, {
    children: /*#__PURE__*/_jsx(InfoIcon, {
      className: "o-ui-alert-negative-icon",
      size: "lg"
    })
  });

  const primaryButtonMarkup = /*#__PURE__*/_jsx(Button, {
    autoFocus: isNil(autoFocusButton) || autoFocusButton === "primary",
    disabled: primaryButtonDisabled,
    onClick: handlePrimaryButtonClick,
    variant: variant === "destructive" ? "danger" : "primary",
    children: primaryButtonLabel
  });

  const secondaryButtonMarkup = !isNilOrEmpty(secondaryButtonLabel) && /*#__PURE__*/_jsx(Button, {
    autoFocus: autoFocusButton === "secondary",
    disabled: secondaryButtonDisabled,
    onClick: handleSecondaryButtonClick,
    variant: "secondary",
    children: secondaryButtonLabel
  });

  const cancelButtonMarkup = !isNilOrEmpty(cancelButtonLabel) && /*#__PURE__*/_jsx(Button, {
    autoFocus: autoFocusButton === "cancel",
    onClick: handleCancelButtonClick,
    variant: "secondary",
    children: cancelButtonLabel
  });

  const buttonsMarkup = isNil(secondaryButtonMarkup) && isNil(cancelButtonMarkup) ? primaryButtonMarkup : /*#__PURE__*/_jsxs(ButtonGroup, {
    children: [cancelButtonMarkup, secondaryButtonMarkup, primaryButtonMarkup]
  });
  return /*#__PURE__*/_jsxs(Dialog, { ...mergeProps(rest, {
      as,
      dismissable: false,
      ref: forwardedRef,
      role: "alertdialog",
      size: "sm",
      zIndex
    }),
    children: [heading, warningIconMarkup, negativeIconMarkup, content, buttonsMarkup]
  });
}
InnerAlert.defaultElement = DefaultElement;
export const Alert = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerAlert, { ...props,
  forwardedRef: ref
}));